import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Package, Zap, Truck, MapPin, CheckCircle } from 'lucide-react';
import { DASHBOARD_SIGNUP_URL, YOUTUBE_URL } from '@/config/links';

const HeroSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  const journeySteps = [
    { icon: Package, message: 'Order picked up', trail: '📦', color: 'from-blue-500 to-cyan-600' },
    { icon: Truck, message: 'In transit', trail: '🚚', color: 'from-purple-500 to-pink-600' },
    { icon: MapPin, message: 'Out for delivery', trail: '📍', color: 'from-blue-700 to-red-600' },
    { icon: CheckCircle, message: 'Delivered', trail: '✅', color: 'from-green-500 to-emerald-600' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % journeySteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [journeySteps.length]);

  const markerPositions = [
    { x: 178, y: 96 }, // Order picked up (near Point A)
    { x: 266, y: 42 }, // In transit (top-mid)
    { x: 340, y: 44 }, // Out for delivery (top-right)
    { x: 404, y: 88 }, // Delivered (near Point B)
  ];
  const ActiveIcon = journeySteps[activeStep].icon;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-blue-300/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-300/20 to-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-gradient-to-br from-blue-100/30 to-blue-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Geometric Shapes */}
        <div className="absolute top-40 right-20 w-20 h-20 border-2 border-blue-300/30 rounded-lg rotate-45"></div>
        <div className="absolute bottom-40 left-20 w-16 h-16 border-2 border-blue-500/30 rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-900 px-4 py-2 rounded-full mb-6">
              <Zap className="w-4 h-4" />
              <span className="text-sm font-semibold">Fast & Reliable</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-800 via-blue-900 to-blue-950 bg-clip-text text-transparent">
                Empowering Your
              </span>
              <br />
              <span className="text-gray-900">eCommerce Deliveries</span>
              <br />
              <span className="bg-gradient-to-r from-blue-800 via-blue-900 to-blue-950 bg-clip-text text-transparent">
                with Precision
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Partner with us for seamless shipping solutions. From order processing to last-mile delivery, 
              we ensure efficiency, speed, and customer satisfaction at every step.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center lg:items-start space-y-4 sm:space-y-0 sm:space-x-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-8 py-6 text-lg"
                onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
              >
                Join Us Today <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-blue-800 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg"
                onClick={() => { window.open(YOUTUBE_URL, '_blank', 'noopener,noreferrer'); }}
              >
                Watch Demo
              </Button>
            </div>
            
            <div className="mt-12 flex items-center justify-center lg:justify-start space-x-8 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <Package className="w-5 h-5 text-blue-800" />
                <span>24/7 Support</span>
              </div>
              <div className="h-4 w-px bg-gray-300"></div>
              <span>No Setup Fees</span>
              <div className="h-4 w-px bg-gray-300"></div>
              <span>Cancel Anytime</span>
            </div>
          </div>
          
          {/* Right Side - Animated Journey */}
          <div className="relative h-96 lg:h-[500px] flex items-center justify-center">
            <div className="relative h-[280px] w-full max-w-[560px] rounded-2xl">
              <div
                className="absolute inset-0 rounded-2xl opacity-80"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(107,114,128,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(107,114,128,0.06) 1px, transparent 1px)',
                  backgroundSize: '56px 56px',
                }}
              />

              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 520 260" fill="none" aria-hidden="true">
                <path
                  d="M92 124 Q268 21 444 124"
                  stroke="#1d4ed8"
                  strokeWidth="4"
                  strokeDasharray="12 14"
                  style={{ animation: 'dashFlow 0.65s linear infinite' }}
                  strokeLinecap="round"
                  opacity="0.9"
                />
              </svg>

              <div className="absolute left-[40px] top-[84px] text-center">
                <div className="mx-auto flex h-[58px] w-[58px] items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                <p className="mt-3 text-[1.35rem] font-semibold text-gray-700">Seller</p>
              </div>

              <div className="absolute right-[20px] top-[84px] text-center">
                <div className="mx-auto flex h-[58px] w-[58px] items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                <p className="mt-3 text-[1.35rem] font-semibold text-gray-700">Customer</p>
              </div>

              <div
                className="absolute z-20"
                style={{
                  left: `${markerPositions[activeStep].x}px`,
                  top: `${markerPositions[activeStep].y}px`,
                  transform: 'translate(-50%, -50%)',
                  transition: 'left 800ms cubic-bezier(0.34, 1.56, 0.64, 1), top 800ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <div className="w-fit mx-auto">
                  <div className="mb-3 mx-auto inline-flex w-fit items-center justify-center overflow-hidden rounded-full border-2 border-blue-200 bg-white py-[17px] px-[19px] shadow-[0_8px_16px_rgba(15,23,42,0.12)]">
                    <div className="flex items-center justify-center gap-2 text-center">
                      <p className="text-center whitespace-nowrap text-[1.075rem] font-semibold leading-none text-slate-900">
                        {journeySteps[activeStep].message}
                      </p>
                      <span className="inline-flex min-w-[14px] items-center justify-center text-[0.9rem] leading-none">
                        {journeySteps[activeStep].trail}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br ${journeySteps[activeStep].color} text-white shadow-xl`}
                    style={{ animation: 'markerBounce 1.54s cubic-bezier(0.34, 1.56, 0.64, 1) infinite' }}
                  >
                    <ActiveIcon className="h-10 w-10" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes dashFlow {
          to {
            stroke-dashoffset: -26;
          }
        }

        @keyframes markerBounce {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          35% {
            transform: translateY(-13px) scale(1.08);
          }
          55% {
            transform: translateY(-7px) scale(1.04);
          }
          75% {
            transform: translateY(-10px) scale(1.06);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;